import { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Image,
  Pagination,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import { CheckOutlined, CloseOutlined, RedoOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/lib/table';
import { refreshPendingCounts } from 'layouts/pending-counts';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import { Ellipsis, NoWrap } from 'pages/star-shared/components/status-tags';
import { imageUrl } from 'pages/star-shared/utils';
import type {
  StudioQueueItem,
  StudioQueueKind,
  StudioRequestResult,
  StudioRequestRow,
} from '../contract';
import {
  STUDIO_PAGES,
  StudioPaging,
  applyStudioRequest,
  approveStudioRequest,
  bulkApproveStudioRequests,
  cancelStudioRequest,
  rejectStudioRequest,
} from '../api';
import { hasStudioErrorCode, mapStudioError } from '../studio-error';
import {
  BULK_MIXED_TOOLTIP,
  EMPTY_TEXT,
  REASON_MIN,
  REQUEST_KIND_LABEL,
  STUDIO_BULK_MAX,
  UID_ONLY_TAG,
  UNAPPLIED_LABEL,
  WRITTEN_NO_LOG_TAG,
  WRITTEN_NO_LOG_TOOLTIP,
  flagLabel,
  requestStatusColor,
  requestStatusLabel,
} from '../labels';
import { formatAgo, formatDateTime, formatKc } from '../format';
import MemberCell from './member-cell';
import styles from '../styles.module.scss';

/** Dòng của bảng: đề nghị trong hàng đợi (Q2) hoặc đề nghị có trạng thái (S25, Q3). */
export type RequestsTableRow = StudioQueueItem | StudioRequestRow;

/**
 * queue: hàng đợi "Chờ Admin" (Q2) — cột Cờ + nút Duyệt / Từ chối / Thử ghi lại, có thể chọn nhiều;
 * history: lịch sử đề nghị (Q3) — thêm Loại, Trạng thái, Người duyệt, Lý do từ chối;
 * mine: đề nghị của Studio mình (S25, tab Cần xử lý của Quản lý) — như history, bỏ Người đề nghị, có nút Huỷ.
 */
export type RequestsTableMode = 'queue' | 'history' | 'mine';

export interface RequestsTableProps {
  rows: RequestsTableRow[];
  loading?: boolean;
  mode: RequestsTableMode;
  /** Cột Studio. Mặc định: có, trừ chế độ `mine`. */
  showStudio?: boolean;
  /** Ô chọn + nút "Duyệt đã chọn (n)" (Q7). Chỉ có tác dụng ở chế độ `queue`. */
  selectable?: boolean;
  /** Gọi sau mỗi thao tác thành công (tải lại bảng). Bộ đếm menu tự làm mới (refreshPendingCounts). */
  onChanged?: () => void;
  pagination?: StudioPaging;
  emptyText?: string;
}

const hasStatus = (row: RequestsTableRow): row is StudioRequestRow =>
  (row as StudioRequestRow).status !== undefined;
const queueKindOf = (row: RequestsTableRow): StudioQueueKind =>
  (row as StudioQueueItem).kind ?? (row.requestKind === 'KC_MANUAL' ? 'STUDIO_KC' : 'STUDIO_MEMBER');
/** Mã cho biết dòng đã được xử lý ở nơi khác. */
const STALE_CODES = [
  'STUDIO_REQUEST_NOT_PENDING',
  'STUDIO_REQUEST_NOT_APPLICABLE',
  'STUDIO_REQUEST_ALREADY_WRITTEN',
  'STUDIO_REQUEST_NOT_FOUND',
];
/**
 * "Đã duyệt – chưa ghi được số" (chỉ KC_MANUAL). Dòng lịch sử: đọc `status`. Dòng hàng đợi Q2 không có
 * `status` và hợp đồng không định nghĩa `applied` cho dòng PENDING, nên KHÔNG suy từ `applied = false`
 * (đề nghị đang chờ sẽ bị gắn nhầm thẻ đỏ): chỉ nhận khi có dấu hiệu đã duyệt — `canApply` (Admin, mục
 * 7.2: dòng này có canApprove = false, canApply = true) hoặc `written`.
 */
const isUnapplied = (row: RequestsTableRow) => {
  if (row.applied || row.requestKind !== 'KC_MANUAL') return false;
  if (hasStatus(row)) return row.status === 'APPROVED';
  return row.canApply || row.written;
};

function notifyResult(result: StudioRequestResult | null | undefined, done: string) {
  const extra = (result?.result || {}) as { applyWarning?: string; applyError?: string };
  // Q5 trả 200 với dòng đã bị Admin khác từ chối / huỷ trong lúc chạy đua (mục 4.6): không báo thành công.
  if (result && result.status !== 'APPROVED') {
    notification.warning({
      message: `Đề nghị #${result.id} đang ở trạng thái "${requestStatusLabel(result)}".`,
      description: extra.applyError || result.reviewNote || undefined,
      duration: 8,
    });
    return;
  }
  const unapplied = result?.status === 'APPROVED' && !result.applied;
  if (result && result.requestKind === 'KC_MANUAL' && unapplied) {
    notification.warning({
      message: `${done} nhưng chưa ghi được số kim cương.`,
      description: extra.applyError || 'Bấm "Thử ghi lại" để ghi số.',
      duration: 8,
    });
    return;
  }
  if (extra.applyWarning) {
    notification.warning({
      message: `${done}. Cần Admin xem lại.`,
      description: extra.applyWarning,
      duration: 8,
    });
    return;
  }
  notification.success({ message: `${done}.` });
}

/**
 * Bảng đề nghị của Quản lý Studio (StudioRequestBase; mục 7.2, 8.7.2, 8.7.8), dùng ở trang Đề nghị chờ duyệt
 * và tab Cần xử lý. Tự làm các thao tác: Duyệt (Q5, ghi chú tuỳ chọn), Từ chối (Q6, lý do ≥ 5 ký tự),
 * Thử ghi lại (Q8), Huỷ (Q4), Duyệt đã chọn (Q7, cùng một loại việc, tối đa 20).
 */
const RequestsTable = ({
  rows,
  loading,
  mode,
  showStudio,
  selectable,
  onChanged,
  pagination,
  emptyText,
}: RequestsTableProps) => {
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [applyingId, setApplyingId] = useState<number | null>(null);
  const withStudio = showStudio ?? mode !== 'mine';
  const withSelect = selectable && mode === 'queue';

  const done = () => {
    setSelectedKeys([]);
    refreshPendingCounts();
    onChanged?.();
  };
  /** Dòng đã đổi ở nơi khác (Admin khác vừa xử lý, số vừa ghi xong): tải lại để thấy trạng thái mới. */
  const reloadIfStale = (err: unknown) => {
    if (STALE_CODES.some((code) => hasStudioErrorCode(err, code))) done();
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedKeys.includes(r.id)),
    [rows, selectedKeys]
  );
  const selectedKinds = Array.from(new Set(selectedRows.map(queueKindOf)));
  const bulkBlocked =
    selectedKinds.length > 1
      ? BULK_MIXED_TOOLTIP
      : selectedRows.length > STUDIO_BULK_MAX
      ? `Mỗi lần duyệt tối đa ${STUDIO_BULK_MAX} đề nghị.`
      : null;

  const openApprove = (row: RequestsTableRow) =>
    setAction({
      title: 'Duyệt đề nghị',
      description: (
        <span>
          #{row.id} · {row.title}
        </span>
      ),
      confirmText: 'Duyệt',
      onSubmit: async (note) => {
        try {
          const result = await approveStudioRequest(row.id, note);
          notifyResult(result, `Đã duyệt đề nghị #${row.id}`);
          done();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          reloadIfStale(err);
          throw err;
        }
      },
    });

  const openReject = (row: RequestsTableRow) =>
    setAction({
      title: 'Từ chối đề nghị',
      description: (
        <span>
          #{row.id} · {row.title}
        </span>
      ),
      noteRequired: true,
      noteLabel: `Lý do từ chối (ít nhất ${REASON_MIN} ký tự)`,
      confirmText: 'Từ chối',
      danger: true,
      onSubmit: async (note) => {
        if ((note || '').length < REASON_MIN) {
          notification.error({ message: `Lý do cần ít nhất ${REASON_MIN} ký tự.` });
          throw new Error('REASON_TOO_SHORT');
        }
        try {
          await rejectStudioRequest(row.id, note as string);
          notification.success({ message: `Đã từ chối đề nghị #${row.id}.` });
          done();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          // Số đã ghi trong lúc chờ (409 STUDIO_REQUEST_ALREADY_WRITTEN): tải lại để dòng hiện
          // "Số đã ghi, thiếu nhật ký" và chỉ còn nút Thử ghi lại (mục 8.7.2).
          reloadIfStale(err);
          throw err;
        }
      },
    });

  const openCancel = (row: RequestsTableRow) =>
    setAction({
      title: 'Huỷ đề nghị',
      description: (
        <span>
          #{row.id} · {row.title}
        </span>
      ),
      noteLabel: 'Lý do huỷ (không bắt buộc)',
      confirmText: 'Huỷ đề nghị',
      danger: true,
      onSubmit: async (note) => {
        try {
          await cancelStudioRequest(row.id, note);
          notification.success({ message: `Đã huỷ đề nghị #${row.id}.` });
          done();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          reloadIfStale(err);
          throw err;
        }
      },
    });

  const runApply = async (row: RequestsTableRow) => {
    setApplyingId(row.id);
    try {
      const result = await applyStudioRequest(row.id);
      notifyResult(result, `Đã ghi số cho đề nghị #${row.id}`);
      done();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
      reloadIfStale(err);
    } finally {
      setApplyingId(null);
    }
  };

  const openBulk = () => {
    const ids = selectedRows.filter((r) => r.canApprove).map((r) => r.id);
    const kind = selectedKinds[0];
    if (!ids.length || !kind || bulkBlocked) return;
    setAction({
      title: `Duyệt ${ids.length} đề nghị đã chọn`,
      description: 'Ghi chú (nếu có) áp dụng cho mọi đề nghị đã chọn. Từ chối thì làm từng đề nghị.',
      confirmText: `Duyệt (${ids.length})`,
      onSubmit: async (note) => {
        try {
          const res = await bulkApproveStudioRequests({ kind, ids, note });
          const results = res?.results || [];
          const ok = results.filter((r) => r.ok);
          const failed = results.filter((r) => !r.ok);
          const unapplied = ok.filter((r) => r.applied === false).length;
          const summary = `Đã duyệt ${ok.length}/${results.length || ids.length} đề nghị`;
          if (failed.length || unapplied) {
            notification.warning({
              message: unapplied ? `${summary} (${unapplied} chưa ghi được số).` : `${summary}.`,
              description: failed.length ? (
                <div>
                  {failed.map((r) => (
                    <div key={r.id}>
                      #{r.id}: {r.message || mapStudioError({ message: r.code })}
                    </div>
                  ))}
                </div>
              ) : undefined,
              duration: 8,
            });
          } else {
            notification.success({ message: `${summary}.` });
          }
          done();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          throw err;
        }
      },
    });
  };

  const renderFlags = (row: RequestsTableRow) => {
    const tags = row.flags.map((code) => (
      <Tag key={code} color="gold">
        {flagLabel(code)}
      </Tag>
    ));
    if (isUnapplied(row)) {
      tags.push(
        row.written ? (
          <Tooltip key="written" title={WRITTEN_NO_LOG_TOOLTIP}>
            <Tag color="red">{WRITTEN_NO_LOG_TAG}</Tag>
          </Tooltip>
        ) : (
          <Tag key="unapplied" color="red">
            {UNAPPLIED_LABEL}
          </Tag>
        )
      );
    }
    return tags.length ? <span className={styles.tags}>{tags}</span> : '—';
  };

  const renderActions = (row: RequestsTableRow) => {
    const buttons: ReactNode[] = [];
    if (mode === 'mine') {
      if (hasStatus(row) && row.canCancel) {
        buttons.push(
          <Button key="cancel" size="small" icon={<StopOutlined />} onClick={() => openCancel(row)}>
            Huỷ
          </Button>
        );
      }
    } else {
      const blocked = row.blockedReason || undefined;
      if (row.canApprove) {
        buttons.push(
          <Button
            key="approve"
            size="small"
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => openApprove(row)}
          >
            Duyệt
          </Button>
        );
      } else if (mode === 'queue' && !isUnapplied(row) && blocked) {
        // SMOD / người không duyệt được: nút mờ, lý do ở tooltip (mục 8.7.2).
        buttons.push(
          <Tooltip key="approve" title={blocked}>
            <Button size="small" icon={<CheckOutlined />} disabled>
              Duyệt
            </Button>
          </Tooltip>
        );
      }
      if (row.canReject) {
        buttons.push(
          <Button
            key="reject"
            size="small"
            danger
            icon={<CloseOutlined />}
            onClick={() => openReject(row)}
          >
            Từ chối
          </Button>
        );
      } else if (mode === 'queue' && blocked && (row.written || !row.canApprove)) {
        buttons.push(
          <Tooltip key="reject" title={blocked}>
            <Button size="small" danger icon={<CloseOutlined />} disabled>
              Từ chối
            </Button>
          </Tooltip>
        );
      }
      if (row.canApply) {
        buttons.push(
          <Popconfirm
            key="apply"
            title="Ghi lại số kim cương của đề nghị này?"
            okText="Thử ghi lại"
            cancelText="Để sau"
            onConfirm={() => runApply(row)}
          >
            <Button size="small" icon={<RedoOutlined />} loading={applyingId === row.id}>
              Thử ghi lại
            </Button>
          </Popconfirm>
        );
      }
    }
    return buttons.length ? <Space size={4} wrap>{buttons}</Space> : null;
  };

  const withHistory = mode !== 'queue';
  const columns: ColumnsType<RequestsTableRow> = [
    {
      title: 'Người được đề nghị',
      key: 'subject',
      width: 220,
      render: (_, r) => (
        <MemberCell
          nickName={r.subject?.nickName || r.subjectName}
          uid={r.subjectUid}
          avatar={r.subject?.avatar}
          size={32}
          sub={r.subjectUid ? `UID ${r.subjectUid}` : null}
          extra={!r.subject ? <Tag>{UID_ONLY_TAG}</Tag> : undefined}
        />
      ),
    },
    ...(withHistory
      ? ([
          {
            title: 'Loại',
            key: 'requestKind',
            width: 150,
            render: (_, r) => REQUEST_KIND_LABEL[r.requestKind] || r.requestKind,
          },
        ] as ColumnsType<RequestsTableRow>)
      : []),
    ...(withStudio
      ? ([
          {
            title: 'Studio',
            key: 'studio',
            width: 170,
            render: (_, r) => (
              <div>
                <Link to={STUDIO_PAGES.detail(r.studio.id)}>
                  {r.studio.title || `Studio #${r.studio.id}`}
                </Link>
                {r.currentStudio && r.currentStudio.id !== r.studio.id && (
                  <div className={styles.muted}>
                    Đang ở: {r.currentStudio.title || `Studio #${r.currentStudio.id}`}
                  </div>
                )}
              </div>
            ),
          },
        ] as ColumnsType<RequestsTableRow>)
      : []),
    {
      title: 'Nội dung',
      key: 'title',
      width: 280,
      render: (_, r) => (
        <div>
          <div className={styles.cellTitle}>{r.title}</div>
          {r.context?.length > 0 && (
            <ul className={styles.context}>
              {r.context.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      ),
    },
    // Chế độ `mine` (Quản lý xem đề nghị của mình) chỉ có cột của mục 8.7.8: Loại, Người, Nội dung,
    // Trạng thái, Người duyệt, Lý do từ chối, Huỷ (+ Gửi lúc). Số KC đã nằm trong `title`.
    ...(mode !== 'mine'
      ? ([
          {
            title: 'KC',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (_, r) => (r.amount ? <NoWrap>{formatKc(r.amount.value)}</NoWrap> : '—'),
          },
          {
            title: 'Minh chứng',
            key: 'evidence',
            width: 100,
            render: (_, r) =>
              r.evidenceUrl ? (
                <Image
                  src={imageUrl(r.evidenceUrl)}
                  width={48}
                  height={48}
                  className={styles.thumb}
                />
              ) : (
                '—'
              ),
          },
          {
            title: 'Người đề nghị',
            key: 'requester',
            width: 150,
            render: (_, r) => r.requester?.name || '—',
          },
        ] as ColumnsType<RequestsTableRow>)
      : []),
    {
      title: mode === 'queue' ? 'Chờ từ' : 'Gửi lúc',
      key: 'createdAt',
      width: 130,
      render: (_, r) => (
        <Tooltip title={formatDateTime(r.createdAt)}>
          <NoWrap>{formatAgo(r.createdAt)}</NoWrap>
        </Tooltip>
      ),
    },
    ...(withHistory
      ? ([
          {
            title: 'Trạng thái',
            key: 'status',
            width: 190,
            render: (_, r) =>
              hasStatus(r) ? (
                <Tag color={requestStatusColor(r)}>{requestStatusLabel(r)}</Tag>
              ) : (
                '—'
              ),
          },
          {
            title: 'Người duyệt',
            key: 'reviewer',
            width: 160,
            render: (_, r) =>
              hasStatus(r) && r.reviewer ? (
                <div>
                  <div>{r.reviewer.name}</div>
                  <div className={styles.muted}>{formatDateTime(r.reviewedAt)}</div>
                </div>
              ) : (
                '—'
              ),
          },
          {
            title: 'Lý do từ chối',
            key: 'reviewNote',
            width: 200,
            render: (_, r) =>
              hasStatus(r) && (r.status === 'REJECTED' || r.status === 'CANCELLED') ? (
                <Ellipsis value={r.reviewNote} width={200} />
              ) : (
                '—'
              ),
          },
        ] as ColumnsType<RequestsTableRow>)
      : []),
    ...(mode !== 'mine'
      ? ([
          {
            title: 'Cờ',
            key: 'flags',
            width: 190,
            render: (_, r) => renderFlags(r),
          },
        ] as ColumnsType<RequestsTableRow>)
      : []),
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right',
      width: mode === 'mine' ? 90 : 230,
      render: (_, r) => renderActions(r),
    },
  ];

  const bulkCount = selectedRows.filter((r) => r.canApprove).length;

  return (
    <div>
      {withSelect && (
        <div className={styles.bulkBar}>
          <Tooltip title={bulkBlocked || undefined}>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={!bulkCount || !!bulkBlocked}
              onClick={openBulk}
            >
              Duyệt đã chọn ({bulkCount})
            </Button>
          </Tooltip>
          {selectedRows.length > 0 && (
            <Button type="link" onClick={() => setSelectedKeys([])}>
              Bỏ chọn
            </Button>
          )}
        </div>
      )}
      <div className="custom-table">
        <Table<RequestsTableRow>
          columns={columns}
          rowKey="id"
          dataSource={rows}
          loading={loading}
          pagination={false}
          locale={{
            emptyText:
              emptyText || (mode === 'queue' ? EMPTY_TEXT.requestsQueue : EMPTY_TEXT.requestsMine),
          }}
          rowSelection={
            withSelect
              ? {
                  selectedRowKeys: selectedKeys,
                  onChange: setSelectedKeys,
                  getCheckboxProps: (r) => ({ disabled: !r.canApprove }),
                }
              : undefined
          }
          scroll={{ x: mode === 'mine' ? 1300 : withHistory ? 1900 : 1500 }}
        />
        {pagination && pagination.total > pagination.pageSize && (
          <Pagination
            showSizeChanger={false}
            showLessItems
            current={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
          />
        )}
      </div>
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default RequestsTable;
